/* eslint-disable react-hooks/exhaustive-deps, react/forbid-component-props, no-param-reassign */
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Carousel } from 'react-responsive-carousel';
import { REACT_CAROUSEL_CONFIG } from '../../../../brands/default/config';
import { updateDod } from '../../../../helpers/analytics';
import { getUrlFromDeal } from '../../../../helpers/deals';
import { showVideo } from '../../../../helpers/video';
import CarouselImageBlock from '../carousel-common/CarouselImageBlock';
import CarouselVideo from '../carousel-common/CarouselVideo';
import CarouselSide from './CarouselSide';
import CarouselThumbs from './CarouselThumbs';

const CarouselArrow = dynamic(
  () => import('../carousel-common/CarouselArrow'),
  {
    ssr: false,
  },
);

const CarouselBlock = ({
  deal = {},
  showImageOverlays = false,
  showControls = false,
  showScrim = false,
  onClick,
  thumbCount,
  isClosed = false,
  single = false,
  invert = false,
  carouselType = 'horizontalScroller',
  disableClickAction = false,
  lazy = true,
  href = null,
  showSingle = false,
}) => {
  const [currentImage, setCurrentImage] = useState(0);
  const hasVideo = showVideo(deal);
  const router = useRouter();
  const first = deal.images ? deal.images[0] : {};

  const dealUrl =
    href ||
    getUrlFromDeal({
      deal,
      originPath: router.asPath,
    });

  useEffect(() => {
    carouselType = `${carouselType}position`;
    updateDod({ carouselType: currentImage });
  }, [currentImage]);

  if (Object.keys(deal).length === 0) return null;

  const inverseNext = () => {
    setCurrentImage(currentImage + 1);
  };

  const inversePrevious = () => {
    setCurrentImage(currentImage - 1);
  };

  const renderCarouselWrapper = (items) => (
    <>
      <div className="row carousel__row">
        <div className="col-8 carousel__col">
          <Carousel
            autoPlay={false}
            className="wow-carousel wow-carousel-three"
            emulateTouch
            infiniteLoop
            interval={REACT_CAROUSEL_CONFIG.intervalTime}
            onChange={(index) => setCurrentImage(index)}
            selectedItem={currentImage}
            showArrows={false}
            showIndicators={false}
            showStatus={false}
            showThumbs={false}
            swipeable
            transitionTime={REACT_CAROUSEL_CONFIG.transitionTimeTravel}
          >
            {items}
          </Carousel>
        </div>
        <div className="col-4 carousel__col">
          <CarouselSide
            deal={deal}
            onSelect={(index) => setCurrentImage(index)}
            selectedItem={currentImage}
          />
        </div>
        <CarouselArrow onClickHandler={inverseNext} orientation="prev" />
        <CarouselArrow
          extraClasses="right-arrow"
          onClickHandler={inversePrevious}
          orientation="next"
        />
      </div>
      {items.length > 3 && (
        <CarouselThumbs
          deal={deal}
          onSelect={(index) => setCurrentImage(index)}
          selectedItem={currentImage}
          showMoreOnClick={inversePrevious}
          thumbCount={5}
        />
      )}
      <style jsx>{`
        .carousel__row {
          margin: 0 -4px;
          position: relative;
        }
        .carousel__col {
          display: flex;
          padding: 0 4px;
        }
        :global(.carousel__row .flickity-prev-next-button.previous) {
          left: 4px;
        }
        :global(.carousel__row .flickity-prev-next-button.next) {
          right: 4px;
        }
        :global(.wow-carousel.wow-carousel-three) {
          width: 100%;
        }
        :global(.wow-carousel) {
          width: 100%;
        }
      `}</style>
    </>
  );

  const renderImages = (items) =>
    items.map((image, index) =>
      index === 0 && hasVideo ? (
        <CarouselVideo
          deal={deal}
          href={dealUrl}
          isClosed={isClosed}
          key={image.id}
          showControls={showControls}
          showScrim={showScrim}
        />
      ) : (
        <CarouselImageBlock
          currentImage={currentImage}
          deal={deal}
          dealUrl={dealUrl}
          disableClickAction={disableClickAction}
          image={image}
          index={index}
          invert={invert}
          items={items}
          key={image.id || index}
          lazy={lazy}
          onClick={onClick}
          setCurrentImage={setCurrentImage}
          showImageOverlays={showImageOverlays}
          showScrim={showScrim}
          showSingle={showSingle}
          thumbCount={thumbCount}
        />
      ),
    );

  return deal.images?.length > 1 && !single
    ? renderCarouselWrapper(renderImages(deal.images))
    : renderImages([first]);
};

CarouselBlock.propTypes = {
  carouselType: PropTypes.string,
  deal: PropTypes.object,
  disableClickAction: PropTypes.bool,
  href: PropTypes.string,
  invert: PropTypes.bool,
  isClosed: PropTypes.bool,
  lazy: PropTypes.bool,
  onClick: PropTypes.func,
  showControls: PropTypes.bool,
  showImageOverlays: PropTypes.bool,
  showScrim: PropTypes.bool,
  showSingle: PropTypes.bool,
  single: PropTypes.bool,
  thumbCount: PropTypes.number,
};

export default CarouselBlock;
